// Copyright (c) 2026 Michael F. Kamprath
//
// telemetry.js -- anonymous aggregate usage telemetry for the web app.
//
// See docs/METRICS-SPEC.md for the full specification.  This module
// is the single client-side entry point for every telemetry emission
// in the app.  All call sites MUST go through one of the four typed
// helpers (trackAppLoad, trackUiAction, trackSceneStarted,
// trackRoundPlayed) -- raw track() calls are intentionally not
// exported so a typo turns into a compile error rather than a silently
// dropped event.
//
// Gating (any returns true -> full no-op, no network):
//   * getShareStats() === false (user opt-out in settings)
//   * /api/config.accepting_events === false (server kill switch)
//   * /api/config fetch failed (fail-closed)
//
// Batching:
//   * 10 events queued OR 30 s elapsed -> flush via fetch POST
//   * visibilitychange->hidden OR pagehide -> flush via sendBeacon
//     (both listeners route through one idempotent flushViaBeacon())
//
// Retries: exponential [1 s, 2 s, 4 s], max 3 attempts, silent drop.

import { getShareStats, getLocale } from './settings.js';

// --- BCP-47 locale regex --------------------------------------------------
//
// Loose BCP-47: 2-3 letter language, optional 4-letter script
// (Latn/Hans/etc.), optional 2-letter region.  Matches every locale
// the app bundles (`en`, `zh-CN`, `ja`, `ko`) plus anything we'd
// realistically add without requiring a server-side allowlist deploy.
const LOCALE_RE = /^[a-z]{2,3}(-[A-Z][a-z]{3})?(-[A-Z]{2})?$/;

// --- Hard-coded client defaults (overridable by /api/config) -------------
//
// Spec §6.2-6.3: each field is merged independently from the server
// response; an unset-or-malformed server field falls through to the
// value below.  `accepting_events=false` from the server is a hard
// short-circuit; `sampling_rate` is stored but not enforced in v1
// (kept for future head-room if the server ever needs load-shedding
// beyond full shutoff).
const DEFAULT_CONFIG = Object.freeze({
  accepting_events: true,
  sampling_rate: 1.0,
  batch_size: 10,
  flush_interval_ms: 30000,
  retry_delays_ms: Object.freeze([1000, 2000, 4000]),
});

// Minimum gap between two beacon flushes.  Tab-close can fire
// visibilitychange then pagehide in rapid succession; without the
// dedupe window we'd send two identical beacons.
const BEACON_DEDUPE_MS = 100;

// Closed enums -- MUST stay in sync with server's EVENT_SCHEMAS and
// with METRICS-SPEC.md §4.  A mismatch here means the client fails
// validation in DEV and silently drops the event in production.
const MODES = Object.freeze(new Set(['learn', 'quiz']));
const THEMES = Object.freeze(new Set(['sheep', 'cows', 'chicks', 'horses', 'apples', 'flowers']));
const UI_ACTIONS = Object.freeze(new Set([
  'home_learn_tap',
  'home_quiz_tap',
  'settings_gate_start',
  'settings_gate_open',
  'mascot_tap',
]));

// --- Module state ---------------------------------------------------------

/** @type {{ accepting_events: boolean, sampling_rate: number, batch_size: number, flush_interval_ms: number, retry_delays_ms: number[] }} */
let config = { ...DEFAULT_CONFIG, retry_delays_ms: [...DEFAULT_CONFIG.retry_delays_ms] };

/** @type {Array<{ name: string, labels: object }>} */
let queue = [];

/**
 * Lifecycle state:
 *   'pre-config'  -- module initialized, config fetch not yet resolved.
 *                    track() calls queue events but DO NOT dispatch.
 *   'active'      -- config fetched, accepting_events=true.  Normal
 *                    batching + flush behaviour.
 *   'disabled'    -- config fetched with accepting_events=false OR
 *                    config fetch failed.  track() calls are no-ops,
 *                    queue is emptied, no further dispatch.
 *   'destroyed'   -- shutdown; listeners removed.
 */
let lifecycle = 'pre-config';

/** @type {ReturnType<typeof setTimeout> | null} */
let flushTimer = null;

/** Timestamp of the last beacon flush (exit path idempotency).
 *  Sentinel -Infinity means "never flushed yet" so the first call
 *  always clears the dedupe window even when deps.now() starts at 0. */
let lastFlushAt = -Infinity;

/** Injectable deps, rebound by configureTelemetry() for tests. */
let deps = {
  fetch: (typeof window !== 'undefined' && window.fetch) ? window.fetch.bind(window) : globalThis.fetch,
  sendBeacon: (typeof navigator !== 'undefined' && navigator.sendBeacon)
    ? (url, blob) => navigator.sendBeacon(url, blob)
    : null,
  now: () => Date.now(),
  getShareStats,
  getLocale,
  setTimeoutImpl: (fn, ms) => setTimeout(fn, ms),
  clearTimeoutImpl: (t) => clearTimeout(t),
  isDev: () => !!(import.meta.env && import.meta.env.DEV),
};

/** Removable listener handles installed by installLifecycleListeners(). */
let listenerHandles = [];

// --- Public API -----------------------------------------------------------

/**
 * Replace any subset of the module's injectable dependencies.  Tests
 * use this to swap `fetch`, `sendBeacon`, `now`, the shareStats probe,
 * and the timer functions.  Production code does not call this.
 *
 * @param {Partial<typeof deps>} overrides
 */
export function configureTelemetry(overrides) {
  deps = { ...deps, ...overrides };
}

/**
 * Kick off the boot sequence.  Idempotent; first call wins.  Fetches
 * /api/config to resolve the final batching/retry config and the
 * accepting_events kill switch, then transitions lifecycle from
 * 'pre-config' to 'active' or 'disabled'.  Returns the Promise so
 * callers that need "config is resolved before first dispatch"
 * guarantees can await it (app.js does this so scene_started fires
 * after the kill switch decision).
 *
 * @param {string} [configUrl='/api/config']
 * @returns {Promise<void>}
 */
export async function start(configUrl = '/api/config') {
  if (lifecycle !== 'pre-config') return;

  try {
    const resp = await deps.fetch(configUrl);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json();
    _mergeConfig(json);
  } catch (err) {
    // Fail-closed per spec §6.4: any config fetch failure shuts the
    // client down for the rest of the session.  Clear any events that
    // were queued during the fetch window.
    _devWarn('config fetch failed; disabling telemetry', err);
    lifecycle = 'disabled';
    queue = [];
    return;
  }

  if (config.accepting_events === false) {
    lifecycle = 'disabled';
    queue = [];
    return;
  }

  lifecycle = 'active';

  // Install exit-flush listeners now that we know we're going to
  // dispatch.  'pre-config' state doesn't need them (nothing to send).
  installLifecycleListeners();

  // If the config fetch window queued a full batch's worth, dispatch now.
  if (queue.length >= config.batch_size) {
    _flush();
  } else if (queue.length > 0) {
    _armFlushTimer();
  }
}

/**
 * Tear down the module.  Flushes any queued events via sendBeacon (if
 * the lifecycle permits), removes all DOM listeners, resets state.
 */
export function destroy() {
  if (lifecycle === 'destroyed') return;
  // Best-effort final flush before going dark.
  if (lifecycle === 'active' && queue.length > 0) {
    _flushViaBeacon();
  }
  _removeLifecycleListeners();
  if (flushTimer !== null) {
    deps.clearTimeoutImpl(flushTimer);
    flushTimer = null;
  }
  queue = [];
  lifecycle = 'destroyed';
}

// --- Typed emission helpers ----------------------------------------------
//
// Every call site in the app uses exactly ONE of these four.  The
// label-shape validation is per-event: extra keys are rejected in
// DEV (so typos are noisy), missing keys drop the event silently in
// production (so a misrouted call never crashes a game scene).

/**
 * App-boot event.  Fires once per session after the splash promise
 * resolves and the first interactive frame renders.
 *
 * @param {{ pwa_installed: boolean }} labels
 */
export function trackAppLoad(labels) {
  _track('app_load', labels, {
    locale: _checkLocale,
    pwa_installed: _checkBool,
  });
}

/**
 * Non-game UI tap on the splash / home screen.  Fires on every
 * relevant tap.
 *
 * @param {{ action: string }} labels
 */
export function trackUiAction(labels) {
  _track('ui_action', labels, {
    locale: _checkLocale,
    action: _checkEnum(UI_ACTIONS),
  });
}

/**
 * Learn or Quiz scene start (post button tap + scene load complete).
 * One per scene, NOT per round.
 *
 * @param {{ mode: string, theme: string, max_count: number }} labels
 */
export function trackSceneStarted(labels) {
  _track('scene_started', labels, {
    mode: _checkEnum(MODES),
    locale: _checkLocale,
    theme: _checkEnum(THEMES),
    max_count: _checkInt(4, 20),
  });
}

/**
 * Individual Learn or Quiz round.  Fires at round end (success,
 * abandonment, or scene exit).  Quiz-only labels (first_try,
 * wrong_selections) should be omitted or set to null for Learn
 * events.
 *
 * @param {{
 *   mode: string, theme: string, count_value: number,
 *   completed: boolean, side_game_engaged: boolean, object_taps: number,
 *   first_try?: boolean | null, wrong_selections?: number,
 * }} labels
 */
export function trackRoundPlayed(labels) {
  _track('round_played', labels, {
    mode: _checkEnum(MODES),
    locale: _checkLocale,
    theme: _checkEnum(THEMES),
    count_value: _checkInt(1, 20),
    completed: _checkBool,
    side_game_engaged: _checkBool,
    object_taps: _checkInt(0, 10_000),
    first_try: _checkBoolOrNull,
    wrong_selections: _checkInt(0, 10_000),
  });
}

// --- Internal helpers -----------------------------------------------------

/**
 * @param {string} name
 * @param {object} userLabels
 * @param {Record<string, (v: any) => boolean>} schema
 */
function _track(name, userLabels, schema) {
  if (lifecycle === 'disabled' || lifecycle === 'destroyed') return;
  if (!deps.getShareStats()) return;

  // Fill in the implicit `locale` label so call sites don't repeat it.
  const labels = { locale: deps.getLocale(), ...userLabels };

  // Per-label validation.  Unknown labels in userLabels aren't an
  // error (forward compat); missing required labels drop the event.
  for (const [key, check] of Object.entries(schema)) {
    const value = labels[key];
    const required = !(key === 'first_try' || key === 'wrong_selections');
    if (value === undefined) {
      if (required) {
        _devWarn(`telemetry drop: ${name} missing required label ${key}`);
        return;
      }
      continue;
    }
    if (!check(value)) {
      _devWarn(`telemetry drop: ${name}.${key} invalid value`, value);
      return;
    }
  }

  queue.push({ name, labels });

  // Lifecycle gate: queue during pre-config, dispatch after.
  if (lifecycle !== 'active') return;

  if (queue.length >= config.batch_size) {
    _flush();
  } else {
    _armFlushTimer();
  }
}

function _armFlushTimer() {
  if (flushTimer !== null) return;
  flushTimer = deps.setTimeoutImpl(_flush, config.flush_interval_ms);
}

/**
 * Normal-path flush: HTTPS POST with retries.  Clears the queue
 * synchronously so events that arrive during the request go into a
 * fresh batch.
 */
function _flush() {
  if (flushTimer !== null) {
    deps.clearTimeoutImpl(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  if (lifecycle !== 'active') return;
  if (!deps.getShareStats()) {
    queue = [];
    return;
  }

  const batch = queue;
  queue = [];

  const body = JSON.stringify({ events: batch });
  _postWithRetry(body, 0);
}

/**
 * Exit-path flush: sendBeacon, no retries.  Called from BOTH the
 * visibilitychange->hidden listener AND pagehide, guarded by a
 * shared dedupe window (lastFlushAt) so rapid successive fires
 * don't dispatch twice.  See spec §6.2.
 */
function _flushViaBeacon() {
  if (lifecycle !== 'active') return;
  if (!deps.sendBeacon) return;
  const nowMs = deps.now();
  if (nowMs - lastFlushAt < BEACON_DEDUPE_MS) return;
  if (queue.length === 0) {
    lastFlushAt = nowMs;
    return;
  }
  if (!deps.getShareStats()) {
    queue = [];
    lastFlushAt = nowMs;
    return;
  }

  const batch = queue;
  queue = [];
  lastFlushAt = nowMs;

  const body = JSON.stringify({ events: batch });
  try {
    // The Blob wrapper carries Content-Type through so the server's
    // is_json guard still passes.
    const blob = new Blob([body], { type: 'application/json' });
    deps.sendBeacon('/api/telemetry', blob);
  } catch {
    // sendBeacon can throw in edge cases (quota exceeded); swallow.
  }
}

/**
 * POST a batch with exponential-backoff retries.  Retries on 5xx
 * and network/abort errors; drops on 2xx (success) or 4xx (client
 * bug -- retrying won't help).  Max attempts = retry_delays_ms.length.
 *
 * @param {string} body
 * @param {number} attempt  zero-based
 */
function _postWithRetry(body, attempt) {
  if (lifecycle === 'destroyed' || lifecycle === 'disabled') return;

  const controller = (typeof AbortController !== 'undefined')
    ? new AbortController() : null;
  const timeoutId = controller
    ? deps.setTimeoutImpl(() => controller.abort(), 10000)
    : null;

  const req = deps.fetch('/api/telemetry', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    signal: controller ? controller.signal : undefined,
  });

  req.then((resp) => {
    if (timeoutId !== null) deps.clearTimeoutImpl(timeoutId);
    if (resp.ok) return;                              // success
    if (resp.status >= 400 && resp.status < 500) return;  // client bug
    _retry(body, attempt);
  }).catch(() => {
    if (timeoutId !== null) deps.clearTimeoutImpl(timeoutId);
    _retry(body, attempt);
  });
}

function _retry(body, attempt) {
  if (attempt >= config.retry_delays_ms.length) return;   // silent drop
  deps.setTimeoutImpl(() => _postWithRetry(body, attempt + 1),
                      config.retry_delays_ms[attempt]);
}

// --- Config merge ---------------------------------------------------------

function _mergeConfig(serverJson) {
  // Per-field merge with type validation (spec §9).  A malformed
  // field falls through to its default; it never contaminates the
  // other fields.
  if (!serverJson || typeof serverJson !== 'object') return;

  if (typeof serverJson.accepting_events === 'boolean') {
    config.accepting_events = serverJson.accepting_events;
  }
  if (Number.isFinite(serverJson.sampling_rate)
      && serverJson.sampling_rate >= 0 && serverJson.sampling_rate <= 1) {
    config.sampling_rate = serverJson.sampling_rate;
  }
  if (Number.isInteger(serverJson.batch_size) && serverJson.batch_size > 0) {
    config.batch_size = serverJson.batch_size;
  }
  if (Number.isInteger(serverJson.flush_interval_ms) && serverJson.flush_interval_ms > 0) {
    config.flush_interval_ms = serverJson.flush_interval_ms;
  }
  if (Array.isArray(serverJson.retry_delays_ms)
      && serverJson.retry_delays_ms.every((d) => Number.isInteger(d) && d >= 0)) {
    config.retry_delays_ms = serverJson.retry_delays_ms.slice();
  }
}

// --- Lifecycle listeners --------------------------------------------------
//
// Belt-and-suspenders exit flush: listen to BOTH visibilitychange
// (fires reliably on mobile backgrounding) AND pagehide (fires
// reliably on desktop tab close + BFCache entry), deduped by
// lastFlushAt so rapid successive fires don't double-dispatch.

function installLifecycleListeners() {
  if (typeof document === 'undefined' && typeof window === 'undefined') return;
  _removeLifecycleListeners();

  if (typeof document !== 'undefined') {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') _flushViaBeacon();
    };
    document.addEventListener('visibilitychange', onVisibility);
    listenerHandles.push(() => document.removeEventListener('visibilitychange', onVisibility));
  }

  if (typeof window !== 'undefined') {
    const onPagehide = () => _flushViaBeacon();
    window.addEventListener('pagehide', onPagehide);
    listenerHandles.push(() => window.removeEventListener('pagehide', onPagehide));
  }
}

function _removeLifecycleListeners() {
  for (const off of listenerHandles) {
    try { off(); } catch { /* no-op */ }
  }
  listenerHandles = [];
}

// --- Per-label type checks -----------------------------------------------

function _checkLocale(v) {
  return typeof v === 'string' && LOCALE_RE.test(v);
}

function _checkBool(v) {
  return typeof v === 'boolean';
}

function _checkBoolOrNull(v) {
  return v === null || typeof v === 'boolean';
}

function _checkEnum(set) {
  return (v) => typeof v === 'string' && set.has(v);
}

function _checkInt(lo, hi) {
  return (v) => Number.isInteger(v) && v >= lo && v <= hi;
}

function _devWarn(...args) {
  if (!deps.isDev()) return;
  // eslint-disable-next-line no-console
  console.warn('[telemetry]', ...args);
}

// --- Test hooks -----------------------------------------------------------

/** @internal reset ALL module state for test isolation. */
export function __resetForTests() {
  queue = [];
  config = { ...DEFAULT_CONFIG, retry_delays_ms: [...DEFAULT_CONFIG.retry_delays_ms] };
  lifecycle = 'pre-config';
  lastFlushAt = -Infinity;
  if (flushTimer !== null) {
    try { deps.clearTimeoutImpl(flushTimer); } catch { /* no-op */ }
    flushTimer = null;
  }
  _removeLifecycleListeners();
  // Reset deps to production defaults; tests that want overrides
  // call configureTelemetry() after this.
  deps = {
    fetch: (typeof window !== 'undefined' && window.fetch) ? window.fetch.bind(window) : globalThis.fetch,
    sendBeacon: (typeof navigator !== 'undefined' && navigator.sendBeacon)
      ? (url, blob) => navigator.sendBeacon(url, blob)
      : null,
    now: () => Date.now(),
    getShareStats,
    getLocale,
    setTimeoutImpl: (fn, ms) => setTimeout(fn, ms),
    clearTimeoutImpl: (t) => clearTimeout(t),
    isDev: () => !!(import.meta.env && import.meta.env.DEV),
  };
}

/** @internal expose the in-flight queue for assertions. */
export function __getQueue() { return queue; }

/** @internal expose current config for assertions. */
export function __getConfig() { return config; }

/** @internal expose lifecycle state. */
export function __getLifecycle() { return lifecycle; }

/** @internal manually trigger the exit-flush path from tests. */
export function __flushViaBeacon() { _flushViaBeacon(); }
